// skipcq: JS-W1028, JS-0128
import React, { useState, useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Snackbar from '@mui/material/Snackbar';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import DashboardLayout from '../../components/layout/DashboardLayout';
import ButtonHeader from '../../components/widgets/ButtonHeader';
import SearchTextField from '../../components/common/SearchTextField';
import Alert from '../../components/widgets/Alert';
import BeerCard from '../../components/beer/BeerCard';
import BeerDupesSection from '../../components/beer/BeerDupesSection';
import useBeerIndex from '../../hooks/useBeerIndex';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import useBreweryLocationLabelsForBeers from '../../hooks/useBreweryLocationLabelsForBeers';
import useEventDefinitionsList from '../../hooks/useEventDefinitionsList';
import AddBeersToEventDialog from './AddBeersToEventDialog';
import AddBeerDialog from '../../components/beer/AddBeerDialog';
import ImportBeerDialog from './sheetImport/ImportBeerDialog';
import Link from '@mui/material/Link';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { BREWERY_BEERS_TAB_INDEX } from '../../constants/breweryBeersTabIndex';

const BEERS_TAB_INDEX = 0;
const DUPES_TAB_INDEX = 1;
const DUPES_TAB_PARAM = 'dupes';

function readBeerListTab(searchParams) {
  if (searchParams.get('tab') === DUPES_TAB_PARAM) {
    return DUPES_TAB_INDEX;
  }
  return BEERS_TAB_INDEX;
}

const searchBarBoxSx = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  width: '100%',
  gap: 1,
  px: 1,
};

const centerSpinnerBoxSx = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '50vh',
};

function BeerListToolbar({
  searchText,
  onSearchChange,
  searchLoading,
  hasSelectedBeers,
  onAddToEvent,
  onAddBeer,
  onImportBeer,
  showBeerActions,
}) {
  return (
    <ButtonHeader sticky fullWidth dense>
      <Box sx={searchBarBoxSx}>
        {showBeerActions && (
          <SearchTextField
            value={searchText}
            onChange={onSearchChange}
            label="Search Beers"
            loading={searchLoading}
            placeholder="Search by beer name..."
            margin="4px 8px"
            width="min(100%, 300px)"
          />
        )}
        {showBeerActions && (
          <Box sx={{ marginLeft: 'auto', display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Button variant="outlined" color="primary" onClick={onImportBeer}>
              Import Beer
            </Button>
            <Button variant="contained" color="primary" onClick={onAddBeer}>
              Add Beer
            </Button>
            <Button
              variant="contained"
              onClick={onAddToEvent}
              disabled={!hasSelectedBeers}
            >
              Add to Event
            </Button>
          </Box>
        )}
      </Box>
    </ButtonHeader>
  );
}

function BeerListResults({
  loading,
  error,
  beers,
  selectedBeerIds,
  onToggleBeerSelect,
  breweryNameById,
  groupedBreweryIds,
  locationLabelsError,
  emptyMessage = 'No beers found.',
}) {
  if (loading) {
    return (
      <Box sx={centerSpinnerBoxSx}>
        <CircularProgress data-testid="main-loading-spinner" />
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      </Box>
    );
  }
  if (!beers.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2">{emptyMessage}</Typography>
      </Box>
    );
  }
  return (
    <>
      {locationLabelsError ? (
        <Box sx={{ px: 2, pb: 1 }}>
          <Typography variant="body2" color="error">
            {locationLabelsError}
          </Typography>
        </Box>
      ) : null}
      <Grid container spacing={2} sx={{ pt: 2 }}>
        {beers.map((beer) => (
          <Grid item xs={12} sm={6} md={4} key={beer.id}>
            <BeerCard
              beer={beer}
              selected={selectedBeerIds.includes(beer.id)}
              onToggleSelect={onToggleBeerSelect}
              onEdit={null}
              breweryNameById={breweryNameById}
              groupedBreweryIds={groupedBreweryIds}
            />
          </Grid>
        ))}
      </Grid>
    </>
  );
}

/**
 * BeerList
 * Lists beers via Typesense (search API key in the browser), same config as breweries.
 * Browse uses name sort; search uses relevance. Missing env shows a shared config error.
 */
function BeerList() {
  const pageTitle = 'Beer';
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = useMemo(() => readBeerListTab(searchParams), [searchParams]);
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebouncedValue(searchText.trim(), 300);

  const [selectedBeerIds, setSelectedBeerIds] = useState([]);
  const [addToEventOpen, setAddToEventOpen] = useState(false);
  const [addBeerOpen, setAddBeerOpen] = useState(false);
  const [importBeerOpen, setImportBeerOpen] = useState(false);
  const [listRefreshKey, setListRefreshKey] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
    autoHideDuration: 6000,
  });

  const searchOptions = useMemo(
    () => ({
      pageSize: 50,
      refreshKey: listRefreshKey,
      enabled: activeTab === BEERS_TAB_INDEX,
    }),
    [listRefreshKey, activeTab],
  );
  const searchQuery = debouncedSearch || '*';

  const { beers, loading, error } = useBeerIndex(searchQuery, searchOptions);
  const {
    breweryNameById,
    groupedBreweryIds,
    error: locationLabelsError,
  } = useBreweryLocationLabelsForBeers(beers);
  const {
    events: eventList,
    loading: eventsLoading,
    error: eventsError,
    appendFeaturedBeersToEvent,
  } = useEventDefinitionsList();

  const handleTabChange = useCallback((unusedEvent, nextTab) => {
    const nextParams = new window.URLSearchParams(searchParams);
    if (nextTab === DUPES_TAB_INDEX) {
      nextParams.set('tab', DUPES_TAB_PARAM);
    } else {
      nextParams.delete('tab');
    }
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleSearchChange = useCallback((event) => {
    setSearchText(event.target.value);
  }, []);

  const handleToggleBeerSelect = useCallback((beerId, isSelected) => {
    if (!beerId) return;
    setSelectedBeerIds((prev) => {
      if (isSelected) {
        if (prev.includes(beerId)) return prev;
        return [...prev, beerId];
      }
      return prev.filter((id) => id !== beerId);
    });
  }, []);

  const handleOpenAddToEvent = useCallback(() => {
    setAddToEventOpen(true);
  }, []);

  const handleCloseAddToEvent = useCallback(() => {
    setAddToEventOpen(false);
  }, []);

  const handleCloseSnackbar = useCallback((event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  const handleOpenAddBeer = useCallback(() => {
    setAddBeerOpen(true);
  }, []);

  const handleCloseAddBeer = useCallback(() => {
    setAddBeerOpen(false);
  }, []);

  const handleOpenImportBeer = useCallback(() => {
    setImportBeerOpen(true);
  }, []);

  const handleCloseImportBeer = useCallback(() => {
    setImportBeerOpen(false);
  }, []);

  const handleAddBeerSuccess = useCallback((payload) => {
    setListRefreshKey((key) => key + 1);
    if (payload.outcome === 'updated') {
      setSnackbar({
        open: true,
        severity: 'success',
        autoHideDuration: 6000,
        message: `Beer updated: ${payload.beerName} (${payload.beerId})`,
      });
      return;
    }
    setSnackbar({
      open: true,
      severity: 'success',
      autoHideDuration: 10000,
      message: (
        <Box>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Beer added. It may take a moment to show up in this list while search updates.
          </Typography>
          <Link
            component={RouterLink}
            to={`/brewery/${payload.breweryId}`}
            state={{ initialTabIndex: BREWERY_BEERS_TAB_INDEX }}
            underline="hover"
          >
            View in brewery beers
          </Link>
        </Box>
      ),
    });
  }, []);

  const handleImportSuccess = useCallback(() => {
    setListRefreshKey((key) => key + 1);
    setSnackbar({
      open: true,
      severity: 'success',
      autoHideDuration: 8000,
      message: 'Beer import completed. The list may take a moment to update.',
    });
  }, []);

  const handleConfirmAddToEvent = useCallback(
    async (eventId, beerIds) => {
      try {
        await appendFeaturedBeersToEvent(eventId, beerIds);
        setSelectedBeerIds([]);
        setSnackbar({
          open: true,
          message: 'Beers added to the event.',
          severity: 'success',
          autoHideDuration: 6000,
        });
      } catch (appendError) {
        setSnackbar({
          open: true,
          message: 'Could not add beers to the event. Try again.',
          severity: 'error',
          autoHideDuration: 6000,
        });
        throw appendError;
      }
    },
    [appendFeaturedBeersToEvent],
  );

  const showBeerActions = activeTab === BEERS_TAB_INDEX;

  return (
    <DashboardLayout title={pageTitle}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="Beer page tabs">
          <Tab label="Beers" id="beer-tab-beers" aria-controls="beer-tabpanel-beers" />
          <Tab label="Beer Dupes" id="beer-tab-dupes" aria-controls="beer-tabpanel-dupes" />
        </Tabs>
      </Box>
      <BeerListToolbar
        searchText={searchText}
        onSearchChange={handleSearchChange}
        searchLoading={loading}
        hasSelectedBeers={selectedBeerIds.length > 0}
        onAddToEvent={handleOpenAddToEvent}
        onAddBeer={handleOpenAddBeer}
        onImportBeer={handleOpenImportBeer}
        showBeerActions={showBeerActions}
      />
      <Container fixed>
        {activeTab === BEERS_TAB_INDEX && (
          <Box
            role="tabpanel"
            id="beer-tabpanel-beers"
            aria-labelledby="beer-tab-beers"
          >
            <BeerListResults
              loading={loading}
              error={error}
              beers={beers}
              selectedBeerIds={selectedBeerIds}
              onToggleBeerSelect={handleToggleBeerSelect}
              breweryNameById={breweryNameById}
              groupedBreweryIds={groupedBreweryIds}
              locationLabelsError={locationLabelsError}
            />
          </Box>
        )}
        {activeTab === DUPES_TAB_INDEX && (
          <Box
            role="tabpanel"
            id="beer-tabpanel-dupes"
            aria-labelledby="beer-tab-dupes"
            sx={{ pt: 2 }}
          >
            <BeerDupesSection globalScope />
          </Box>
        )}
      </Container>
      <AddBeersToEventDialog
        open={addToEventOpen}
        onClose={handleCloseAddToEvent}
        events={eventList}
        eventsLoading={eventsLoading}
        eventsError={eventsError}
        selectedBeerIds={selectedBeerIds}
        onConfirmAdd={handleConfirmAddToEvent}
      />
      <AddBeerDialog
        open={addBeerOpen}
        onClose={handleCloseAddBeer}
        onSuccess={handleAddBeerSuccess}
      />
      <ImportBeerDialog
        open={importBeerOpen}
        onClose={handleCloseImportBeer}
        onImportSuccess={handleImportSuccess}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.autoHideDuration ?? 6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

BeerList.propTypes = {};

export default BeerList;
